import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Area, AreaChart,
} from "recharts";
import { Loader2, TrendingUp, Award, Zap, FileText, CheckCircle2, AlertCircle, Clock, BarChart3 } from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const COLORS = ["#014d7d", "#178a55", "#c8841a", "#b84040", "#6941c6", "#0e7490", "#e55d25"];
const STATUS_COLORS_MAP = { todo: "#94a3b8", draft: "#f59e0b", in_review: "#014d7d", done: "#10b981", published: "#8b5cf6", blocked: "#ef4444", in_progress: "#3b82f6", completed: "#10b981" };

export const ProjectAnalytics = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [timing, setTiming] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [analyticsRes, timingRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/analytics`, { withCredentials: true }),
          axios.get(`${API}/projects/${projectId}/stage-timing`, { withCredentials: true }),
        ]);
        setData(analyticsRes.data && typeof analyticsRes.data === 'object' ? analyticsRes.data : null);
        setTiming(timingRes.data && typeof timingRes.data === 'object' ? timingRes.data : null);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [projectId]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-foreground/45" /></div>;
  if (!data) return <div className="text-center py-16 text-foreground/45 text-sm">Unable to load analytics</div>;

  const statusData = Object.entries(data.status_distribution)?.map(([k, v]) => ({ name: k.replace("_", " "), value: v, fill: STATUS_COLORS_MAP[k] || "#94a3b8" }));
  const stageData = Object.entries(data.stage_distribution)?.map(([k, v]) => ({ name: k, tasks: v }));
  const lifecycleData = Object.entries(data.lifecycle_distribution)?.map(([k, v]) => ({ name: k.replace("_", " "), value: v, fill: STATUS_COLORS_MAP[k] || "#94a3b8" }));
  const autoData = [
    { name: "Automated", value: data.automation_ratio.automated, fill: "#014d7d" },
    { name: "Manual", value: data.automation_ratio.manual, fill: "#94a3b8" },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="analytics-tab">
      {/* Quality Score Hero */}
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-1 bg-gradient-to-br from-[#014d7d] to-[#178a55] rounded-2xl p-5 text-white" data-testid="quality-score-card">
          <div className="flex items-center gap-2 mb-2 opacity-80">
            <Award className="w-4 h-4" />
            <span className="text-[11px] font-semibold uppercase tracking-wider">Quality Score</span>
          </div>
          <p className="text-4xl font-heading font-bold">{data.quality_score}<span className="text-lg opacity-60">/100</span></p>
          <p className="text-[11px] opacity-60 mt-1">Based on completion, reviews & engagement</p>
        </div>
        {[
          { label: "Total Tasks", val: data.summary.total_tasks, icon: CheckCircle2, color: "text-[#014d7d]", bg: "bg-[#014d7d]/5" },
          { label: "Artifacts", val: data.summary.total_artifacts, icon: FileText, color: "text-purple-600", bg: "bg-purple-50" },
          { label: "Review Cycles", val: data.summary.review_cycles, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
        ]?.map(s => (
          <div key={s.label} className="bg-white border border-border/50 rounded-xl p-4">
            <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-2`}>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <p className="text-2xl font-heading font-bold text-foreground">{s.val}</p>
            <p className="text-[10px] text-foreground/50 font-semibold uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Trend Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Task Completion Trend */}
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="task-completion-trend">
          <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> Task Completions (14 days)
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={data.task_completion_trend}>
              <defs>
                <linearGradient id="taskGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#014d7d" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#014d7d" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Area type="monotone" dataKey="count" stroke="#014d7d" strokeWidth={2} fill="url(#taskGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Volume */}
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="activity-trend">
          <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <BarChart3 className="w-3 h-3" /> Activity Volume (14 days)
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.activity_volume_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="count" fill="#178a55" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Distribution Charts Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Status Distribution */}
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="status-distribution">
          <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Task Status</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={2}>
                {statusData?.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Lifecycle Distribution */}
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="lifecycle-distribution">
          <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Asset Lifecycle</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={lifecycleData.length ? lifecycleData : [{ name: "No data", value: 1, fill: "#e5e7eb" }]} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={2}>
                {(lifecycleData.length ? lifecycleData : [{ fill: "#e5e7eb" }])?.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Automation Ratio */}
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="automation-ratio">
          <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Automation Ratio</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={autoData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={2}>
                {autoData?.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stage Distribution Bar */}
      {stageData.length > 0 && (
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="stage-distribution">
          <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Tasks per Stage</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stageData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 9, fill: "#94a3b8" }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} width={100} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="tasks" fill="#014d7d" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Time-in-Stage / Bottleneck */}
      {timing && timing.stage_metrics?.length > 0 && (
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="bottleneck-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" /> Time-in-Stage & Bottleneck Analysis
            </h3>
            {timing.bottleneck && (
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-lg bg-red-50 text-red-600 border border-red-200" data-testid="bottleneck-badge">
                Bottleneck: {timing.bottleneck} ({timing.bottleneck_avg_hours}h avg)
              </span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={timing.stage_metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} label={{ value: "Avg Hours", angle: -90, position: "insideLeft", style: { fontSize: 9, fill: "#94a3b8" } }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => [`${v}h`, "Avg Time"]} />
              <Bar dataKey="avg_hours" radius={[4, 4, 0, 0]}>
                {timing.stage_metrics?.map((entry, i) => (
                  <Cell key={i} fill={entry.stage === timing.bottleneck ? "#ef4444" : "#014d7d"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Throughput metrics */}
          <div className="flex items-center gap-6 mt-4 pt-3 border-t border-border/30">
            <div className="text-center">
              <p className="text-lg font-heading font-bold text-foreground">{timing.throughput.tasks_per_day}</p>
              <p className="text-[10px] text-foreground/50 font-semibold">Tasks/Day</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-heading font-bold text-foreground">{timing.throughput.days_active}</p>
              <p className="text-[10px] text-foreground/50 font-semibold">Days Active</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-heading font-bold text-foreground">{timing.throughput.total_completed}</p>
              <p className="text-[10px] text-foreground/50 font-semibold">Completed</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
